<template lang="pug">
//- Legend List 
ul.charts-css.legend.legend-inline.legend-line.justify-center.fade-in(
  v-if="screen.graph.legend"
  :style="`${screen.graph.width ? `width: ${screen.graph.width};` : 'width: 100%'}`"
  )
  li(
    v-for="(row) in screen.graph.legend"
    :class="row.color ? `${row.color}` : ''"
  ) {{ row.text }}
table.charts-css.show-heading.show-labels.show-data-axes.show-primary-axis.data-spacing-10.datasets-spacing-10.shadow-lg.fade-in.p-4(
  :style="`--aspect-ratio: ${aspectRatio}; ${screen.graph.width ? `width: ${screen.graph.width};` : 'width: 100%'}`"
  :class="{ 'long-label': isLongLabel && screen.graph.type === 'bar', [screen.graph.type]: true, 'multiple': Array.isArray(screen.graph.items[0].data), 'labels-align-block-center': screen.graph.type === 'column', 'labels-align-inline-center': screen.graph.type === 'bar', 'reduce-motion': suspend.reducedMotion }"
  )

  caption.font-bold.leading-10(
    v-if="screen.graph.heading || screen.graph.subheading"
    :class="{ 'heading': screen.graph.heading, 'subheading': screen.graph.subheading }"
    ) {{ screen.graph.heading || screen.graph.subheading }}

  //- Column or Bar graph
  tbody(v-if="screen.graph.type === 'bar' || screen.graph.type === 'column'")
    tr(v-for="(row in screen.graph.items")
      th.prose.dark_text-white(scope="row" v-if="row.label") {{ row.label }}

      //- Single data item row
      td(
        :style="{ '--size': row.data, backgroundColor: row.color ? `rgb(var(--${row.color}))` : '' }"
        v-if="(screen.graph.type === 'bar' || screen.graph.type === 'column') && !Array.isArray(row.data)"
      )
        span.prose.data.font-bold.my-2.mx-auto.p-1(
          v-if="row.value"
          :style="{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }"
          :class="{ 'me-2': screen.graph.type === 'bar' }"
        ) {{ row.value }}

      //- Multiple datasets item row
      td.text-center(
        v-else=""
        v-for="(data, index) in row.data"
        :style="{ '--size': data, backgroundColor: screen.graph.legend[index].color ? `rgb(var(--${screen.graph.legend[index].color}))` : '' }"
      )
        span.prose.data.font-bold.mx-auto.p-1(
          v-if="row.value"
          :style="{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }"
          :class="{ 'me-2': screen.graph.type === 'bar', 'my-2': screen.graph.type === 'column' }"
        ) {{ row.value[index] }}

  //- Pie graph
  tbody(v-if="screen.graph.type === 'pie'")
    tr(v-for="(row, index) in screen.graph.items" :key="index")
      th.prose.dark_text-white(scope="row" v-if="row.label") {{ row.label }}
      td(
        :style="`--start: ${row.start}; --end: ${row.end}; ${screen.graph.legend[index].color ? `--c: rgb(var(--${screen.graph.legend[index].color}));` : ''}`"
      )
        span.prose.data.font-bold(
          v-if="row.value"
        ) {{ row.value }}

</template>

<script setup lang="ts">
import { computed } from 'vue';
import { twMerge } from 'tailwind-merge';
import useSuspend from '../lib/useSuspend';

const suspend = useSuspend();

interface UcScreen {
  graph: {
    type: string;
    items: {
      label: string;
      data: any[];
      value: string[];
    }[];
    'aspect-ratio'?: number;
    legend?: {
      text: string;
      color: string;
    }[];
    width?: string;
    heading?: string;
    subheading?: string;
  };
}

const props = defineProps<{ editPath: string; screen: UcScreen }>();

const aspectRatio = computed(() => {
  return props.screen.graph['aspect-ratio'] || 4;
});

const maxLabelLength = computed(() => {
  return props.screen.graph.items.reduce((acc, item) => {
    if (item.label) {
      return Math.max(acc, item.label.length);
    }
    return acc;
  }, 0);
});

const isLongLabel = computed(() => {
  return maxLabelLength.value >= 10;
});
</script>

<style scoped>
@import 'https://cdn.jsdelivr.net/npm/charts.css/dist/charts.min.css';

/* Animations */
.charts-css.-in.column tbody td {
  transform-origin: bottom;
  animation: revealing-columns 1.5s linear forwards;
}

@keyframes revealing-columns {
  0% {
    transform: scaleY(0);
  }

  100% {
    transform: scaleY(1);
  }
}

.charts-css.-in.bar tbody td {
  transform-origin: left;
  animation: revealing-bars 1.5s linear forwards;
}

@keyframes revealing-bars {
  0% {
    transform: scaleX(0);
  }

  100% {
    transform: scaleX(1);
  }
}

.charts-css.reduce-motion.-in.column tbody td,
.charts-css.reduce-motion.-in.bar tbody td {
  animation: none;
}

/* Override default charts-css basic settings */
.charts-css {
  --primary-axis-color: rgba(0, 0, 0, 0.15);
}

.dark .charts-css {
  --primary-axis-color: white;
  --data-axes-color: white;
}

.dark .charts-css.legend li {
  color: white;
}

.charts-css.long-label {
  --labels-size: 200px;
}

.charts-css caption {
  margin-bottom: 18px;
}

/* Default alternate primary colors for bar and column graphs */
.charts-css.column tbody tr:nth-child(odd) td,
.charts-css.bar tbody tr:nth-child(odd) td {
  background-color: rgb(var(--color-primary-200));
}

.charts-css.column tbody tr:nth-child(even) td,
.charts-css.bar tbody tr:nth-child(even) td {
  background-color: rgb(var(--color-secondary-200));
}

.dark .charts-css.column tbody tr:nth-child(odd) td,
.dark .charts-css.bar tbody tr:nth-child(odd) td {
  background-color: rgb(var(--color-primary-700));
}

.dark .charts-css.column tbody tr:nth-child(even) td,
.dark .charts-css.bar tbody tr:nth-child(even) td {
  background-color: rgb(var(--color-secondary-700));
}

/* Default primary color shades for multiple datasets in bar/column graphs and their legends */
.charts-css.column.multiple tbody tr td:nth-of-type(9n + 1),
.charts-css.bar.multiple tbody tr td:nth-of-type(9n + 1),
.charts-css.legend li:nth-child(1):before {
  background-color: rgb(var(--color-primary-100));
}

.charts-css.column.multiple tbody tr td:nth-of-type(9n + 2),
.charts-css.bar.multiple tbody tr td:nth-of-type(9n + 2),
.charts-css.legend li:nth-child(2):before {
  background-color: rgb(var(--color-primary-200));
}

.charts-css.column.multiple tbody tr td:nth-of-type(9n + 3),
.charts-css.bar.multiple tbody tr td:nth-of-type(9n + 3),
.charts-css.legend li:nth-child(3):before {
  background-color: rgb(var(--color-primary-300));
}

.charts-css.column.multiple tbody tr td:nth-of-type(9n + 4),
.charts-css.bar.multiple tbody tr td:nth-of-type(9n + 4),
.charts-css.legend li:nth-child(4):before {
  background-color: rgb(var(--color-primary-400));
}

.charts-css.column.multiple tbody tr td:nth-of-type(9n + 5),
.charts-css.bar.multiple tbody tr td:nth-of-type(9n + 5),
.charts-css.legend li:nth-child(5):before {
  background-color: rgb(var(--color-primary-500));
}

.charts-css.column.multiple tbody tr td:nth-of-type(9n + 6),
.charts-css.bar.multiple tbody tr td:nth-of-type(9n + 6),
.charts-css.legend li:nth-child(6):before {
  background-color: rgb(var(--color-primary-600));
}

.charts-css.column.multiple tbody tr td:nth-of-type(9n + 7),
.charts-css.bar.multiple tbody tr td:nth-of-type(9n + 7),
.charts-css.legend li:nth-child(7):before {
  background-color: rgb(var(--color-primary-700));
}

.charts-css.column.multiple tbody tr td:nth-of-type(9n + 8),
.charts-css.bar.multiple tbody tr td:nth-of-type(9n + 8),
.charts-css.legend li:nth-child(8):before {
  background-color: rgb(var(--color-primary-800));
}

.charts-css.column.multiple tbody tr td:nth-of-type(9n + 9),
.charts-css.bar.multiple tbody tr td:nth-of-type(9n + 9),
.charts-css.legend li:nth-child(9):before {
  background-color: rgb(var(--color-primary-900));
}

/* Default primary color shades for pie graphs */
.charts-css.pie tbody tr:nth-of-type(9n + 1) td {
  --c: rgb(var(--color-primary-100));
}

.charts-css.pie tbody tr:nth-of-type(9n + 2) td {
  --c: rgb(var(--color-primary-200));
}

.charts-css.pie tbody tr:nth-of-type(9n + 3) td {
  --c: rgb(var(--color-primary-300));
}

.charts-css.pie tbody tr:nth-of-type(9n + 4) td {
  --c: rgb(var(--color-primary-400));
}

.charts-css.pie tbody tr:nth-of-type(9n + 5) td {
  --c: rgb(var(--color-primary-500));
}

.charts-css.pie tbody tr:nth-of-type(9n + 6) td {
  --c: rgb(var(--color-primary-600));
}

.charts-css.pie tbody tr:nth-of-type(9n + 7) td {
  --c: rgb(var(--color-primary-700));
}

.charts-css.pie tbody tr:nth-of-type(9n + 8) td {
  --c: rgb(var(--color-primary-800));
}

.charts-css.pie tbody tr:nth-of-type(9n + 9) td {
  --c: rgb(var(--color-primary-900));
}

/* Allow custom color classes for legends */
.charts-css.legend li.color-red-100:before {
  background-color: rgb(var(--color-red-100));
}

.charts-css.legend li.color-red-200:before {
  background-color: rgb(var(--color-red-200));
}

.charts-css.legend li.color-red-300:before {
  background-color: rgb(var(--color-red-300));
}

.charts-css.legend li.color-red-400:before {
  background-color: rgb(var(--color-red-400));
}

.charts-css.legend li.color-red-500:before {
  background-color: rgb(var(--color-red-500));
}

.charts-css.legend li.color-red-600:before {
  background-color: rgb(var(--color-red-600));
}

.charts-css.legend li.color-red-700:before {
  background-color: rgb(var(--color-red-700));
}

.charts-css.legend li.color-red-800:before {
  background-color: rgb(var(--color-red-800));
}

.charts-css.legend li.color-red-900:before {
  background-color: rgb(var(--color-red-900));
}

.charts-css.legend li.color-orange-100:before {
  background-color: rgb(var(--color-orange-100));
}

.charts-css.legend li.color-orange-200:before {
  background-color: rgb(var(--color-orange-200));
}

.charts-css.legend li.color-orange-300:before {
  background-color: rgb(var(--color-orange-300));
}

.charts-css.legend li.color-orange-400:before {
  background-color: rgb(var(--color-orange-400));
}

.charts-css.legend li.color-orange-500:before {
  background-color: rgb(var(--color-orange-500));
}

.charts-css.legend li.color-orange-600:before {
  background-color: rgb(var(--color-orange-600));
}

.charts-css.legend li.color-orange-700:before {
  background-color: rgb(var(--color-orange-700));
}

.charts-css.legend li.color-orange-800:before {
  background-color: rgb(var(--color-orange-800));
}

.charts-css.legend li.color-orange-900:before {
  background-color: rgb(var(--color-orange-900));
}

.charts-css.legend li.color-amber-100:before {
  background-color: rgb(var(--color-amber-100));
}

.charts-css.legend li.color-amber-200:before {
  background-color: rgb(var(--color-amber-200));
}

.charts-css.legend li.color-amber-300:before {
  background-color: rgb(var(--color-amber-300));
}

.charts-css.legend li.color-amber-400:before {
  background-color: rgb(var(--color-amber-400));
}

.charts-css.legend li.color-amber-500:before {
  background-color: rgb(var(--color-amber-500));
}

.charts-css.legend li.color-amber-600:before {
  background-color: rgb(var(--color-amber-600));
}

.charts-css.legend li.color-amber-700:before {
  background-color: rgb(var(--color-amber-700));
}

.charts-css.legend li.color-amber-800:before {
  background-color: rgb(var(--color-amber-800));
}

.charts-css.legend li.color-amber-900:before {
  background-color: rgb(var(--color-amber-900));
}

.charts-css.legend li.color-yellow-100:before {
  background-color: rgb(var(--color-yellow-100));
}

.charts-css.legend li.color-yellow-200:before {
  background-color: rgb(var(--color-yellow-200));
}

.charts-css.legend li.color-yellow-300:before {
  background-color: rgb(var(--color-yellow-300));
}

.charts-css.legend li.color-yellow-400:before {
  background-color: rgb(var(--color-yellow-400));
}

.charts-css.legend li.color-yellow-500:before {
  background-color: rgb(var(--color-yellow-500));
}

.charts-css.legend li.color-yellow-600:before {
  background-color: rgb(var(--color-yellow-600));
}

.charts-css.legend li.color-yellow-700:before {
  background-color: rgb(var(--color-yellow-700));
}

.charts-css.legend li.color-yellow-800:before {
  background-color: rgb(var(--color-yellow-800));
}

.charts-css.legend li.color-yellow-900:before {
  background-color: rgb(var(--color-yellow-900));
}

.charts-css.legend li.color-lime-100:before {
  background-color: rgb(var(--color-lime-100));
}

.charts-css.legend li.color-lime-200:before {
  background-color: rgb(var(--color-lime-200));
}

.charts-css.legend li.color-lime-300:before {
  background-color: rgb(var(--color-lime-300));
}

.charts-css.legend li.color-lime-400:before {
  background-color: rgb(var(--color-lime-400));
}

.charts-css.legend li.color-lime-500:before {
  background-color: rgb(var(--color-lime-500));
}

.charts-css.legend li.color-lime-600:before {
  background-color: rgb(var(--color-lime-600));
}

.charts-css.legend li.color-lime-700:before {
  background-color: rgb(var(--color-lime-700));
}

.charts-css.legend li.color-lime-800:before {
  background-color: rgb(var(--color-lime-800));
}

.charts-css.legend li.color-lime-900:before {
  background-color: rgb(var(--color-lime-900));
}

.charts-css.legend li.color-green-100:before {
  background-color: rgb(var(--color-green-100));
}

.charts-css.legend li.color-green-200:before {
  background-color: rgb(var(--color-green-200));
}

.charts-css.legend li.color-green-300:before {
  background-color: rgb(var(--color-green-300));
}

.charts-css.legend li.color-green-400:before {
  background-color: rgb(var(--color-green-400));
}

.charts-css.legend li.color-green-500:before {
  background-color: rgb(var(--color-green-500));
}

.charts-css.legend li.color-green-600:before {
  background-color: rgb(var(--color-green-600));
}

.charts-css.legend li.color-green-700:before {
  background-color: rgb(var(--color-green-700));
}

.charts-css.legend li.color-green-800:before {
  background-color: rgb(var(--color-green-800));
}

.charts-css.legend li.color-green-900:before {
  background-color: rgb(var(--color-green-900));
}

.charts-css.legend li.color-emerald-100:before {
  background-color: rgb(var(--color-emerald-100));
}

.charts-css.legend li.color-emerald-200:before {
  background-color: rgb(var(--color-emerald-200));
}

.charts-css.legend li.color-emerald-300:before {
  background-color: rgb(var(--color-emerald-300));
}

.charts-css.legend li.color-emerald-400:before {
  background-color: rgb(var(--color-emerald-400));
}

.charts-css.legend li.color-emerald-500:before {
  background-color: rgb(var(--color-emerald-500));
}

.charts-css.legend li.color-emerald-600:before {
  background-color: rgb(var(--color-emerald-600));
}

.charts-css.legend li.color-emerald-700:before {
  background-color: rgb(var(--color-emerald-700));
}

.charts-css.legend li.color-emerald-800:before {
  background-color: rgb(var(--color-emerald-800));
}

.charts-css.legend li.color-emerald-900:before {
  background-color: rgb(var(--color-emerald-900));
}

.charts-css.legend li.color-teal-100:before {
  background-color: rgb(var(--color-teal-100));
}

.charts-css.legend li.color-teal-200:before {
  background-color: rgb(var(--color-teal-200));
}

.charts-css.legend li.color-teal-300:before {
  background-color: rgb(var(--color-teal-300));
}

.charts-css.legend li.color-teal-400:before {
  background-color: rgb(var(--color-teal-400));
}

.charts-css.legend li.color-teal-500:before {
  background-color: rgb(var(--color-teal-500));
}

.charts-css.legend li.color-teal-600:before {
  background-color: rgb(var(--color-teal-600));
}

.charts-css.legend li.color-teal-700:before {
  background-color: rgb(var(--color-teal-700));
}

.charts-css.legend li.color-teal-800:before {
  background-color: rgb(var(--color-teal-800));
}

.charts-css.legend li.color-teal-900:before {
  background-color: rgb(var(--color-teal-900));
}

.charts-css.legend li.color-cyan-100:before {
  background-color: rgb(var(--color-cyan-100));
}

.charts-css.legend li.color-cyan-200:before {
  background-color: rgb(var(--color-cyan-200));
}

.charts-css.legend li.color-cyan-300:before {
  background-color: rgb(var(--color-cyan-300));
}

.charts-css.legend li.color-cyan-400:before {
  background-color: rgb(var(--color-cyan-400));
}

.charts-css.legend li.color-cyan-500:before {
  background-color: rgb(var(--color-cyan-500));
}

.charts-css.legend li.color-cyan-600:before {
  background-color: rgb(var(--color-cyan-600));
}

.charts-css.legend li.color-cyan-700:before {
  background-color: rgb(var(--color-cyan-700));
}

.charts-css.legend li.color-cyan-800:before {
  background-color: rgb(var(--color-cyan-800));
}

.charts-css.legend li.color-cyan-900:before {
  background-color: rgb(var(--color-cyan-900));
}

.charts-css.legend li.color-sky-100:before {
  background-color: rgb(var(--color-sky-100));
}

.charts-css.legend li.color-sky-200:before {
  background-color: rgb(var(--color-sky-200));
}

.charts-css.legend li.color-sky-300:before {
  background-color: rgb(var(--color-sky-300));
}

.charts-css.legend li.color-sky-400:before {
  background-color: rgb(var(--color-sky-400));
}

.charts-css.legend li.color-sky-500:before {
  background-color: rgb(var(--color-sky-500));
}

.charts-css.legend li.color-sky-600:before {
  background-color: rgb(var(--color-sky-600));
}

.charts-css.legend li.color-sky-700:before {
  background-color: rgb(var(--color-sky-700));
}

.charts-css.legend li.color-sky-800:before {
  background-color: rgb(var(--color-sky-800));
}

.charts-css.legend li.color-sky-900:before {
  background-color: rgb(var(--color-sky-900));
}

.charts-css.legend li.color-indigo-100:before {
  background-color: rgb(var(--color-indigo-100));
}

.charts-css.legend li.color-indigo-200:before {
  background-color: rgb(var(--color-indigo-200));
}

.charts-css.legend li.color-indigo-300:before {
  background-color: rgb(var(--color-indigo-300));
}

.charts-css.legend li.color-indigo-400:before {
  background-color: rgb(var(--color-indigo-400));
}

.charts-css.legend li.color-indigo-500:before {
  background-color: rgb(var(--color-indigo-500));
}

.charts-css.legend li.color-indigo-600:before {
  background-color: rgb(var(--color-indigo-600));
}

.charts-css.legend li.color-indigo-700:before {
  background-color: rgb(var(--color-indigo-700));
}

.charts-css.legend li.color-indigo-800:before {
  background-color: rgb(var(--color-indigo-800));
}

.charts-css.legend li.color-indigo-900:before {
  background-color: rgb(var(--color-indigo-900));
}

.charts-css.legend li.color-violet-100:before {
  background-color: rgb(var(--color-violet-100));
}

.charts-css.legend li.color-violet-200:before {
  background-color: rgb(var(--color-violet-200));
}

.charts-css.legend li.color-violet-300:before {
  background-color: rgb(var(--color-violet-300));
}

.charts-css.legend li.color-violet-400:before {
  background-color: rgb(var(--color-violet-400));
}

.charts-css.legend li.color-violet-500:before {
  background-color: rgb(var(--color-violet-500));
}

.charts-css.legend li.color-violet-600:before {
  background-color: rgb(var(--color-violet-600));
}

.charts-css.legend li.color-violet-700:before {
  background-color: rgb(var(--color-violet-700));
}

.charts-css.legend li.color-violet-800:before {
  background-color: rgb(var(--color-violet-800));
}

.charts-css.legend li.color-violet-900:before {
  background-color: rgb(var(--color-violet-900));
}

.charts-css.legend li.color-purple-100:before {
  background-color: rgb(var(--color-purple-100));
}

.charts-css.legend li.color-purple-200:before {
  background-color: rgb(var(--color-purple-200));
}

.charts-css.legend li.color-purple-300:before {
  background-color: rgb(var(--color-purple-300));
}

.charts-css.legend li.color-purple-400:before {
  background-color: rgb(var(--color-purple-400));
}

.charts-css.legend li.color-purple-500:before {
  background-color: rgb(var(--color-purple-500));
}

.charts-css.legend li.color-purple-600:before {
  background-color: rgb(var(--color-purple-600));
}

.charts-css.legend li.color-purple-700:before {
  background-color: rgb(var(--color-purple-700));
}

.charts-css.legend li.color-purple-800:before {
  background-color: rgb(var(--color-purple-800));
}

.charts-css.legend li.color-purple-900:before {
  background-color: rgb(var(--color-purple-900));
}

.charts-css.legend li.color-fuchsia-100:before {
  background-color: rgb(var(--color-fuchsia-100));
}

.charts-css.legend li.color-fuchsia-200:before {
  background-color: rgb(var(--color-fuchsia-200));
}

.charts-css.legend li.color-fuchsia-300:before {
  background-color: rgb(var(--color-fuchsia-300));
}

.charts-css.legend li.color-fuchsia-400:before {
  background-color: rgb(var(--color-fuchsia-400));
}

.charts-css.legend li.color-fuchsia-500:before {
  background-color: rgb(var(--color-fuchsia-500));
}

.charts-css.legend li.color-fuchsia-600:before {
  background-color: rgb(var(--color-fuchsia-600));
}

.charts-css.legend li.color-fuchsia-700:before {
  background-color: rgb(var(--color-fuchsia-700));
}

.charts-css.legend li.color-fuchsia-800:before {
  background-color: rgb(var(--color-fuchsia-800));
}

.charts-css.legend li.color-fuchsia-900:before {
  background-color: rgb(var(--color-fuchsia-900));
}

.charts-css.legend li.color-pink-100:before {
  background-color: rgb(var(--color-pink-100));
}

.charts-css.legend li.color-pink-200:before {
  background-color: rgb(var(--color-pink-200));
}

.charts-css.legend li.color-pink-300:before {
  background-color: rgb(var(--color-pink-300));
}

.charts-css.legend li.color-pink-400:before {
  background-color: rgb(var(--color-pink-400));
}

.charts-css.legend li.color-pink-500:before {
  background-color: rgb(var(--color-pink-500));
}

.charts-css.legend li.color-pink-600:before {
  background-color: rgb(var(--color-pink-600));
}

.charts-css.legend li.color-pink-700:before {
  background-color: rgb(var(--color-pink-700));
}

.charts-css.legend li.color-pink-800:before {
  background-color: rgb(var(--color-pink-800));
}

.charts-css.legend li.color-pink-900:before {
  background-color: rgb(var(--color-pink-900));
}

.charts-css.legend li.color-rose-100:before {
  background-color: rgb(var(--color-rose-100));
}

.charts-css.legend li.color-rose-200:before {
  background-color: rgb(var(--color-rose-200));
}

.charts-css.legend li.color-rose-300:before {
  background-color: rgb(var(--color-rose-300));
}

.charts-css.legend li.color-rose-400:before {
  background-color: rgb(var(--color-rose-400));
}

.charts-css.legend li.color-rose-500:before {
  background-color: rgb(var(--color-rose-500));
}

.charts-css.legend li.color-rose-600:before {
  background-color: rgb(var(--color-rose-600));
}

.charts-css.legend li.color-rose-700:before {
  background-color: rgb(var(--color-rose-700));
}

.charts-css.legend li.color-rose-800:before {
  background-color: rgb(var(--color-rose-800));
}

.charts-css.legend li.color-rose-900:before {
  background-color: rgb(var(--color-rose-900));
}
</style>
